/**
 * Меню «Куда пойти» (interactives.md H2; core-loop-v2 §2, такт 2): три
 * группы — жизнь, деньги, риск. Каждое место — строка-документ: имя,
 * дела кнопками ≥ 44px с честной строкой ожидания под каждым
 * (`place.expect.*`). Недоступное место — та же строка без кнопок, причина
 * в самом тексте («Работа — нет работы»), 45 % прозрачности, как у кнопок.
 * Недоступное дело в доступном месте — кнопка `disabled` с причиной.
 * Тап по делу → `game.act`: эффект применяется сразу, без ожидания тика.
 */
import type { Dataset } from '@life/data';
import type { Life } from '@life/engine';
import { t } from '@life/i18n';
import { actionAvailability, placeAvailability, PLACES, type PlaceDef, type PlaceGroup } from '@life/minigames';
import type { Game } from '../state/game';
import { placeOptions } from '../state/turn';

export const PLACE_GROUPS: readonly PlaceGroup[] = ['life', 'money', 'risk'];

export interface PlacesMenuProps {
  game: Game;
  ds: Dataset;
}

function Place({ def, life, ds, game }: { def: PlaceDef; life: Life; ds: Dataset; game: Game }) {
  const opts = placeOptions(life, game.settings.value);
  const place = placeAvailability(def, life, ds, opts);
  const name = t(def.labelKey);
  if (!place.ok) {
    return (
      <li class="place na" data-place={def.id}>
        <span class="place-name">{t('ui.place.unavailable', { name, reason: t(place.reasonKey) })}</span>
      </li>
    );
  }
  return (
    <li class="place" data-place={def.id}>
      <span class="place-name">{name}</span>
      <div class="place-actions">
        {def.actions.map((a) => {
          const ok = actionAvailability(def, a.id, life, ds, opts);
          const label = t(a.labelKey);
          const params = a.expectParams?.(life, ds);
          return (
            <div key={a.id} class="place-action">
              <button type="button" class="btn block act" disabled={!ok.ok} onClick={ok.ok ? () => game.act(def.id, a.id) : undefined}>
                {ok.ok ? label : t('ui.place.unavailable', { name: label, reason: t(ok.reasonKey) })}
              </button>
              <p class="hint expect">{params ? t(a.expectKey, params) : t(a.expectKey)}</p>
            </div>
          );
        })}
      </div>
    </li>
  );
}

export function PlacesMenu({ game, ds }: PlacesMenuProps) {
  const life = game.life.value;
  if (life === null) return null;
  return (
    <section class="places">
      {PLACE_GROUPS.map((group) => (
        <div key={group} class="place-group">
          <div class="eyebrow">{t(`place.group.${group}`)}</div>
          <ul class="place-list">
            {PLACES.filter((p) => p.group === group).map((def) => (
              <Place key={def.id} def={def} life={life} ds={ds} game={game} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
